/*
 * The authenticated app shell (U5) — now a responsive Mantine `AppShell`.
 *
 * Header carries the product wordmark, a mobile `Burger` (toggles the navbar
 * below the `sm` breakpoint), the brand switcher, and the user menu. The navbar
 * is the left nav rail; it collapses to a burger-toggled drawer on mobile. The
 * main content area centers to a readable column but still supports full-bleed
 * mode via the `data-fullbleed` opt-out the launch wizard relies on (see
 * Shell.module.css).
 *
 * Navigation is role-aware — links still show for viewers (they can read those
 * screens) but the screens gate write actions with `can()`. The brand switcher
 * lists every membership and issues a switch-brand token; the user menu carries
 * identity + logout.
 */

import { useState, type ComponentType, type CSSProperties } from "react";
import { NavLink as RouterNavLink, Outlet, useNavigate } from "react-router-dom";
import {
  AppShell,
  Burger,
  Group,
  Menu,
  NavLink,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useAuth } from "../auth/AuthContext";
import { BuildStamp } from "./BuildStamp";
import {
  BrandMark,
  IconBrands,
  IconCredentials,
  IconHygiene,
  IconPerformance,
  IconProducts,
  IconQueue,
  IconTeam,
  IconUsers,
  IconVideo,
} from "./icons";
import styles from "./Shell.module.css";

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  end?: boolean;
  /** Section hue (2026-07-23 colour pass) — spectral token pair from
   *  tokens.css, consumed by Shell.module.css as --nav-hue / --nav-tint. */
  hue: string;
  tint: string;
}

const PRIMARY_NAV: NavItem[] = [
  {
    to: "/",
    label: "Queue",
    icon: IconQueue,
    end: true,
    hue: "var(--color-accent)",
    tint: "var(--color-accent-subtle)",
  },
  {
    to: "/products",
    label: "Products",
    icon: IconProducts,
    hue: "var(--color-spectral-teal)",
    tint: "var(--color-spectral-teal-tint)",
  },
  {
    to: "/hygiene",
    label: "Hygiene",
    icon: IconHygiene,
    hue: "var(--color-spectral-amber)",
    tint: "var(--color-spectral-amber-tint)",
  },
  {
    to: "/video-tickets",
    label: "Video tickets",
    icon: IconVideo,
    hue: "var(--color-spectral-cyan)",
    tint: "var(--color-spectral-cyan-tint)",
  },
  // Creative Analysis (plan 2026-07-22-001 U7): label swap only — the route
  // path stays /performance so existing bookmarks keep working.
  {
    to: "/performance",
    label: "Creative Analysis",
    icon: IconPerformance,
    hue: "var(--color-spectral-violet)",
    tint: "var(--color-spectral-violet-tint)",
  },
];

/** Super-admin-only entry: credential management. */
const CREDENTIALS_NAV: NavItem = {
  to: "/credentials",
  label: "Credentials",
  icon: IconCredentials,
  hue: "var(--color-spectral-cyan)",
  tint: "var(--color-spectral-cyan-tint)",
};

/** Admin-only entry: brand member management. */
const TEAM_NAV: NavItem = {
  to: "/team",
  label: "Team",
  icon: IconTeam,
  hue: "var(--color-spectral-rose)",
  tint: "var(--color-spectral-rose-tint)",
};
/** Super-admin-only entry: the global all-users roster. */
const USERS_NAV: NavItem = {
  to: "/users",
  label: "Users",
  icon: IconUsers,
  hue: "var(--color-spectral-blue)",
  tint: "var(--color-spectral-blue-tint)",
};
/** Super-admin-only entry: cross-brand provisioning. */
const BRANDS_NAV: NavItem = {
  to: "/brands",
  label: "Brands",
  icon: IconBrands,
  hue: "var(--color-spectral-emerald)",
  tint: "var(--color-spectral-emerald-tint)",
};

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function BrandSwitcher() {
  const { memberships, activeBrandId, role, switchBrand } = useAuth();
  const [busy, setBusy] = useState(false);

  if (memberships.length === 0) return null;

  const active = memberships.find((m) => m.brand_id === activeBrandId);

  async function pick(brandId: string) {
    if (brandId === activeBrandId) return;
    setBusy(true);
    try {
      await switchBrand(brandId);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Menu position="bottom-end" withinPortal shadow="md" width={260}>
      <Menu.Target>
        <UnstyledButton
          className={styles.brandTrigger}
          aria-haspopup="menu"
          disabled={busy}
        >
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={500}>
              {active ? active.brand_name || shortId(active.brand_id) : "Select brand"}
            </Text>
            {role && (
              <Text size="xs" c="dimmed" tt="capitalize">
                · {role}
              </Text>
            )}
            <Text size="xs" c="dimmed" aria-hidden="true">
              ▾
            </Text>
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Switch brand</Menu.Label>
        {memberships.map((m) => (
          <Menu.Item
            key={m.brand_id}
            aria-checked={m.brand_id === activeBrandId}
            disabled={busy}
            onClick={() => void pick(m.brand_id)}
            rightSection={m.brand_id === activeBrandId ? "✓" : undefined}
          >
            {m.brand_name || shortId(m.brand_id)}
            <Text component="span" size="xs" c="dimmed" tt="capitalize">
              {" "}
              · {m.role}
            </Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function UserMenu() {
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <Menu position="bottom-end" withinPortal shadow="md" width={240}>
      <Menu.Target>
        <UnstyledButton className={styles.brandTrigger} aria-haspopup="menu">
          <Group gap={6} wrap="nowrap">
            <Stack gap={0} align="flex-end">
              <Text size="sm" fw={500}>
                {user?.email ?? "Account"}
              </Text>
              {role && (
                <Text size="xs" c="dimmed" tt="capitalize">
                  {role}
                </Text>
              )}
            </Stack>
            <Text size="xs" c="dimmed" aria-hidden="true">
              ▾
            </Text>
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Signed in</Menu.Label>
        <Menu.Item disabled>{user?.email ?? "—"}</Menu.Item>
        <Menu.Divider />
        <Menu.Item
          onClick={() => {
            logout();
            navigate("/login", { replace: true });
          }}
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export function Shell() {
  const { can, isSuperAdmin, hasActiveBrand } = useAuth();
  const [opened, { toggle, close }] = useDisclosure(false);

  // The brand-scoped links (Queue/Products/Credentials + Team) only make sense
  // with an active brand — a no-brand super-admin's routes redirect to
  // /brands, so we hide those dead links and show only the super-admin
  // surfaces (Users/Brands). Team is additionally admin-gated; Users + Brands
  // are super-admin-gated. All still server-enforce — this only hides controls
  // the user cannot use from here.
  const navItems: NavItem[] = [
    ...(hasActiveBrand ? PRIMARY_NAV : []),
    ...(hasActiveBrand && can("administer") ? [TEAM_NAV] : []),
    ...(hasActiveBrand && isSuperAdmin ? [CREDENTIALS_NAV] : []),
    ...(isSuperAdmin ? [USERS_NAV, BRANDS_NAV] : []),
  ];

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{
        width: 240,
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header className={styles.header}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="Toggle navigation"
            />
            <Group gap={10} wrap="nowrap">
              <BrandMark size={24} />
              <Text fw={600} size="sm" visibleFrom="xs" lts="-0.01em">
                <Text component="span" fw={600} className={styles.wordmark}>
                  Meta
                </Text>{" "}
                <Text component="span" c="dimmed" fw={400}>
                  launcher
                </Text>
              </Text>
            </Group>
          </Group>
          <Group gap="sm" wrap="nowrap">
            <BrandSwitcher />
            <UserMenu />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" className={styles.navbar}>
        <nav aria-label="Primary">
          <div className={styles.navSection}>Workspace</div>
          <Stack gap={2}>
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  component={RouterNavLink}
                  to={item.to}
                  end={item.end}
                  onClick={close}
                  label={item.label}
                  className={styles.navLink}
                  style={{ "--nav-hue": item.hue, "--nav-tint": item.tint } as CSSProperties}
                  leftSection={<Icon size={16} />}
                />
              );
            })}
          </Stack>
        </nav>
        <BuildStamp />
      </AppShell.Navbar>

      <AppShell.Main>
        <div className={styles.content}>
          <Outlet />
        </div>
      </AppShell.Main>
    </AppShell>
  );
}
